import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useInfiniteHits, useInstantSearch } from 'react-instantsearch'
import { useInView } from 'react-intersection-observer'
import { isEmpty } from 'remeda'
import {
  CardContent,
  CollectionContainer,
  Grid,
} from '@components/ApparelCollection/styles'
import { ApparelProduct } from '@components/ApparelProduct/ApparelProduct'
import { useCategoriesGrid } from '@components/CategoriesGrid/CategoriesGridProvider'
import { useCategoryManager } from '@velasca/hooks/use-category-manager'
import { NoResults } from './styles'
import type { FormattedVariantProps } from './types'
import { formatAlgoliaHit } from './utils'

interface SearchResultsProps {
  showSingleVariant?: boolean
  showProductType?: boolean
}

export const SearchResults = ({
  showSingleVariant = false,
  showProductType = true,
}: SearchResultsProps) => {
  useCategoryManager()

  const { t } = useTranslation('collection-filters')
  const { results, hits, isLastPage, isFirstPage, showMore, showPrevious } =
    useInfiniteHits<FormattedVariantProps>()
  const { status } = useInstantSearch()

  const [loadMoreStartRef, inViewNextStart] = useInView({
    rootMargin: `300px 0px 0px 0px`,
  })

  const [loadMoreEndRef, inViewNextEnd] = useInView({
    rootMargin: `0px 0px 300px 0px`,
  })
  const inViewNext = useMemo(
    () => inViewNextStart || inViewNextEnd,
    [inViewNextStart, inViewNextEnd],
  )

  const { frontImage: frontImageToShow } = useCategoriesGrid()

  // Volevo fare il carica precedente come il carica successivo, ma purtroppo non è uguale.
  // Quando si scrolla verso il basso, supponendo di essere alla pagina n, la logica è sempre vedere la pagina n + 1.
  // Scrollando in cima, invece, dovrei vedere la pagina 1, non la pagina n - 1. Per questo questo pezzo di codice è commentato
  // const [loadPreviousStartRef, inViewPreviousStart] = useInView({
  //   rootMargin: `0px 0px 0px 0px`,
  // })

  // const [loadPreviousEndRef, inViewPreviousEnd] = useInView({
  //   rootMargin: `0px 0px 0px 0px`,
  // })
  // const inViewPrevious = useMemo(() => inViewPreviousStart || inViewPreviousEnd, [inViewPreviousStart, inViewPreviousEnd])

  // Questo useEffect viene usato quando la pagina viene caricata con già i parametri della ricerca.
  // Es: quando un utente clicca su un prodotto e poi torna indietro oppure quando l'url viene condiviso oppure al click su un link.
  // Se il cliente scrolla, viene aggiunto il parametro `page`. Questo useEffect fa tante chiamate quante il valore del parametro page(meno una).
  // Questo perché Algolia carica solo i risultati della pagina corrente.
  // Purtroppo non so come altro fare a ripristinare i risultati all'utente
  // se non pescando tutti i risultati dalla pagina n - 1 fino alla pagina 1.
  // Questo useEffect parte in automatico se ci sono pagine vuote sopra a quella correntemente caricata. Si potrebbe mettere un tasto loadPrevious
  // come fa Gymshark per evitare che il load sia molto lento. Infatti a pagina 10-11, Algolia deve fare 9-10 chiamate
  // e per quanto rapido, il loading dura parecchio. Dopo un click dell'utente sarebbe un pochino più accettabile il loading.
  // Con questa soluzione, comunque ci si perderebbe il punto dello scroll a cui l'utente era arrivato. Infatti se l'utente arriva a pagina 10, ma poi
  // riscrolla verso l'alto andando a vedere i risultati di pagina 6, con il meccanismo del loadPrevious vedrebbe solo i risultati di pagina 10, di fatto
  // "perdendosi" e confondendo l'utente.
  // Da approfondire la questione localStorage per evitare tante chiamate.
  // Il vantaggio di questa soluzione è che funziona per tutti, anche condividendo l'url
  useEffect(() => {
    if (!showPrevious) {
      return
    }

    if (isFirstPage) {
      return
    }

    if (status === 'idle') {
      showPrevious()
    }
  }, [showPrevious, isFirstPage, status])

  useEffect(() => {
    if (isLastPage) {
      return
    }

    // Check che status === 'idle' non dia problemi
    if (inViewNext && status === 'idle') {
      showMore()
    }
  }, [inViewNext, showMore, isLastPage, status])

  const hitsPerPage = results?.hitsPerPage ?? 20

  // Metto tanti skeleton in cima quante sono le pagine "fantasma" e carico i risultati da "sotto in su"
  // (da pagina n - 1 a 1) usando la funzione loadPrevious
  const skeletonBefore = (results?.page || 0) * hitsPerPage

  return (
    <CollectionContainer>
      <Grid>
        {!isFirstPage &&
          skeletonBefore > 0 &&
          [...Array(skeletonBefore)].map((emptyValue, i) => {
            return (
              <CardContent
                key={`filter-result-previous-${results?.page ?? 1}-${String(
                  i,
                )}`}
                // Se si potesse usare il metodo di loadMore anche su loadPrevious, si farebbe così:
                // {...(i === 0 ? { ref: loadPreviousStartRef } : {} )}
                // {...(arr.length - 1 === i ? { ref: loadPreviousEndRef } : {} )}
              >
                <ApparelProduct
                  loading
                  showSingleVariant={showSingleVariant}
                  showProductType={showProductType}
                  frontImageToShow={frontImageToShow}
                  showVariantSelector={false}
                />
              </CardContent>
            )
          })}
        {/* Per evitare di far vedere gli skeleton "sentinella" del load more, quando le hits non sono valorizzate,
        mostro degli skeleton dummy che non fanno triggerare il load more. Altrimenti ogni volta che carico la pagina parte subito un loadMore */}
        {!isEmpty(hits) &&
          hits.map((hit) => {
            return (
              <CardContent key={`filter-result-${String(hit.handle)}`}>
                <ApparelProduct
                  showSingleVariant={showSingleVariant}
                  showProductType={showProductType}
                  frontImageToShow={frontImageToShow}
                  showVariantSelector={false}
                  {...formatAlgoliaHit(hit)}
                />
              </CardContent>
            )
          })}
        {/* See here: https://github.com/algolia/instantsearch/discussions/5358 */}
        {(status === 'loading' || status === 'stalled') &&
          [...Array(hitsPerPage)].map((emptyValue, i) => {
            return (
              <CardContent key={`filter-result-current-${String(i)}`}>
                <ApparelProduct
                  loading
                  showSingleVariant={showSingleVariant}
                  showProductType={showProductType}
                  frontImageToShow={frontImageToShow}
                  showVariantSelector={false}
                />
              </CardContent>
            )
          })}
        {/* TODO: stilizzare l'assenza di risultati */}
        {status === 'idle' && isEmpty(hits) && (
          <NoResults>{t('results', { count: 0 })}</NoResults>
        )}
        {/* Metto solo 3 skeleton per evitarmi tutto il problema del restare "incastrati" in mezzo agli skeleton.
        Ci sono dei casi in cui si rimane bloccati in mezzo ai 20 skeleton perché inView resta a true.
        Lo useEffect quindi non viene scatenato. Una possibile soluzione da esplorare è usare lo status idle.
        Se lo status cambia in idle e inView è ancora true, vuol dire che devo caricare altri risultati ancora */}
        {isLastPage
          ? null
          : [...Array(3)].map((emptyValue, i, arr) => {
              return (
                <CardContent
                  key={`filter-result-next-${results?.page ?? 1}-${String(i)}`}
                  {...(i === 0 ? { ref: loadMoreStartRef } : {})}
                  {...(arr.length - 1 === i ? { ref: loadMoreEndRef } : {})}
                >
                  <ApparelProduct
                    loading
                    showSingleVariant={showSingleVariant}
                    showProductType={showProductType}
                    frontImageToShow={frontImageToShow}
                    showVariantSelector={false}
                  />
                </CardContent>
              )
            })}
      </Grid>
    </CollectionContainer>
  )
}
